/**
 * useKeyboardShortcuts - Global keyboard shortcuts for the game
 *
 * Shortcuts:
 * - WASD/Arrow keys: Pan camera
 * - Space: Pause/Resume simulation (admin only)
 * - F: Follow selected creature
 * - Escape: Deselect creature
 * - ?: Show help
 * - +/-: Zoom in/out
 */

import { useEffect, useCallback } from 'react';
import { useSimulationStore } from '../stores/simulationStore';

interface KeyboardShortcutsOptions {
  onFollowToggle: () => void;
  onDeselect: () => void;
  onShowHelp?: () => void;
  enabled?: boolean;
}

const CAMERA_PAN_SPEED = 50;
const ZOOM_STEP = 0.1;

export function useKeyboardShortcuts({
  onFollowToggle,
  onDeselect,
  onShowHelp,
  enabled = true,
}: KeyboardShortcutsOptions) {
  const setCamera = useSimulationStore((state) => state.setCamera);
  const cameraX = useSimulationStore((state) => state.cameraX);
  const cameraY = useSimulationStore((state) => state.cameraY);
  const cameraZoom = useSimulationStore((state) => state.cameraZoom);
  const selectedCreatureId = useSimulationStore((state) => state.selectedCreatureId);
  const setFollowCreature = useSimulationStore((state) => state.setFollowCreature);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = event.key.toLowerCase();

      switch (key) {
        // Camera pan with WASD
        case 'w':
        case 'arrowup':
          event.preventDefault();
          setCamera(cameraX, cameraY - CAMERA_PAN_SPEED / cameraZoom);
          setFollowCreature(false);
          break;

        case 's':
        case 'arrowdown':
          event.preventDefault();
          setCamera(cameraX, cameraY + CAMERA_PAN_SPEED / cameraZoom);
          setFollowCreature(false);
          break;

        case 'a':
        case 'arrowleft':
          event.preventDefault();
          setCamera(cameraX - CAMERA_PAN_SPEED / cameraZoom, cameraY);
          setFollowCreature(false);
          break;

        case 'd':
        case 'arrowright':
          event.preventDefault();
          setCamera(cameraX + CAMERA_PAN_SPEED / cameraZoom, cameraY);
          setFollowCreature(false);
          break;

        // Zoom with +/-
        case '+':
        case '=':
          event.preventDefault();
          useSimulationStore.setState({
            cameraZoom: Math.min(cameraZoom + ZOOM_STEP, 3),
          });
          break;

        case '-':
        case '_':
          event.preventDefault();
          useSimulationStore.setState({
            cameraZoom: Math.max(cameraZoom - ZOOM_STEP, 0.5),
          });
          break;

        // Follow creature
        case 'f':
          if (selectedCreatureId) {
            event.preventDefault();
            onFollowToggle();
          }
          break;

        // Deselect
        case 'escape':
          if (selectedCreatureId) {
            event.preventDefault();
            onDeselect();
          }
          break;

        // Help
        case '?':
          event.preventDefault();
          onShowHelp?.();
          break;

        default:
          break;
      }
    },
    [cameraX, cameraY, cameraZoom, selectedCreatureId, setCamera, setFollowCreature, onFollowToggle, onDeselect, onShowHelp]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}
